import React, { useRef, useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { TimetableItem } from './subparts/types';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

interface SelectedCell {
  classId: string;
  section: string | null;
  period: string;
  scheduleItems: TimetableItem[];
  className: string;
}

interface DeleteModalProps {
  deleteDialogVisible: boolean;
  setDeleteDialogVisible: (visible: boolean) => void;
  selectedCell: SelectedCell | null;
  setSelectedCell: (cell: SelectedCell | null) => void;
  fetchTimetableData: () => void;
  toast: React.RefObject<Toast | null>;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  deleteDialogVisible,
  setDeleteDialogVisible,
  selectedCell,
  setSelectedCell,
  fetchTimetableData,
  toast,
}) => {
  const { schoolDetails } = useSchoolDetailsStore();
  const [isClosing, setIsClosing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.delete-timetable-modal .p-dialog');
      const mask = document.querySelector('.delete-timetable-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setDeleteDialogVisible(false);
      setIsClosing(false);
    }, 400);
  };

  const handleDeleteClick = (itemId: string) => {
    setItemToDelete(itemId);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);

    try {
      // Get current session ID
      const sessionId = schoolDetails?.current_session?.id;
      
      if (!sessionId) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No active session found',
          life: 5000,
        });
        return;
      }

      // Send delete request with session_id
      await apiClient.delete(`/api/timetable/${itemToDelete}?session_id=${sessionId}`);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Timetable entry deleted successfully',
        life: 4000,
      });
      
      fetchTimetableData();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to delete timetable entry';
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: msg,
        life: 5000,
      });
    } finally {
      setIsDeleting(false);
      setShowConfirmDialog(false);
      setItemToDelete(null);
      handleClose();
      setSelectedCell(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
    setItemToDelete(null);
  };

  return (
    <>
      <style>{`
        .delete-timetable-modal {
          backdrop-filter: blur(4px);
          background-color: rgba(0, 0, 0, 0.25) !important;
          animation: maskFadeIn 0.35s ease;
        }
        
        .delete-timetable-modal.mask-closing {
          animation: maskFadeOut 0.4s ease !important;
        }
        
        @keyframes maskFadeIn {
          from {
            opacity: 0;
            background-color: rgba(0, 0, 0, 0);
          }
          to {
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.25);
          }
        }
        
        @keyframes maskFadeOut {
          from {
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.25);
          }
          to {
            opacity: 0;
            background-color: rgba(0, 0, 0, 0);
          }
        }
        
        .delete-timetable-modal .p-dialog {
          animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          background: transparent !important;
          border: none !important;
        }

        .delete-timetable-modal .p-dialog::before {
          content: '';
          position: absolute;
          inset: -15px;
          background: rgb(255 255 255 / 64%);
          border-radius: 40px;
          z-index: -1;
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }
        
        .delete-timetable-modal .p-dialog.dialog-closing {
          animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        @keyframes dialogOpen {
          from {
            opacity: 0;
            transform: scale(0.92) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes dialogClose {
          from {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
          to {
            opacity: 0;
            transform: scale(0.88) translateY(25px);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>

      <Dialog
        visible={deleteDialogVisible && !isClosing}
        onHide={handleClose}
        showHeader={false}
        style={{
          width: '90vw',
          maxWidth: '520px',
          borderRadius: '32px',
          overflow: 'visible',
          boxShadow: 'none'
        }}
        contentStyle={{
          padding: '32px',
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="delete-timetable-modal"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s ease',
            lineHeight: 1,
            padding: 0,
            zIndex: 10
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = '#1f2937')}
          onMouseOut={(e) => (e.currentTarget.style.color = '#6b7280')}
        >
          ×
        </button>

        {/* Header with Icon */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
          paddingBottom: '20px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            width: '54px',
            height: '54px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
            border: '3px solid white'
          }}>
            <i 
              className="pi pi-exclamation-triangle" 
              style={{
                color: 'white',
                fontSize: '24px'
              }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#1e40af',
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              Manage Timetable Entry
            </div>
            <div style={{
              fontSize: '13px',
              color: '#64748b',
              fontWeight: 500
            }}>
              {selectedCell?.className && (
                <>Class: {selectedCell.className}</>
              )}
              {selectedCell?.section && (
                <> • Section: {selectedCell.section}</>
              )}
              {selectedCell?.period && (
                <> • Period: {selectedCell.period}</>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div>
          {selectedCell?.scheduleItems && selectedCell.scheduleItems.length > 0 ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              marginBottom: '20px'
            }}>
              {selectedCell.scheduleItems.map((item) => (
                <div
                  key={item.id}
                  style={{
                    background: item.slot_type === 'break' ? '#fef3c7' : '#eff6ff',
                    padding: '16px',
                    borderRadius: '12px',
                    border: item.slot_type === 'break' ? '1px solid #fde68a' : '1px solid #bfdbfe',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ 
                        fontWeight: '600', 
                        color: item.slot_type === 'break' ? '#b45309' : '#1e40af', 
                        fontSize: '14px' 
                      }}>
                        {item.subject_name || item.slot_type || 'Unknown'}
                      </span>
                      {item.slot_type && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: '#64748b',
                          background: '#f1f5f9',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          textTransform: 'capitalize'
                        }}>
                          {item.slot_type}
                        </span>
                      )}
                    </div>
                    {item.slot_type !== 'break' && item.teacher_name && (
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Teacher: </span>
                        <span style={{ fontSize: '12px', color: '#1f2937', fontWeight: 500 }}>
                          {item.teacher_name}
                        </span>
                      </div>
                    )}
                    {item.room_number && (
                      <div>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Room: </span>
                        <span style={{ fontSize: '12px', color: '#1f2937', fontWeight: 500 }}>
                          {item.room_number}
                        </span>
                      </div>
                    )}
                  </div>
                  <GlobalButton
                    label="Delete"
                    onClick={() => handleDeleteClick(item.id)}
                    variant="danger"
                    size="small"
                    disabled={isDeleting}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '32px 0',
              color: '#64748b'
            }}>
              <i className="pi pi-info-circle" style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }} />
              <p style={{ margin: 0 }}>No entries found for this period</p>
            </div>
          )}

          {/* Footer */}
          <div style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: '16px',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <GlobalButton
              label="Close"
              onClick={handleClose}
              variant="outline"
              size="small"
            />
          </div>
        </div>

        {/* Mini Confirmation Dialog */}
        {showConfirmDialog && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'fadeIn 0.2s ease'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              maxWidth: '320px',
              width: '90%',
              animation: 'scaleIn 0.2s ease',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                inset: '-12px',
                background: 'rgb(255 255 255 / 48%)',
                borderRadius: '24px',
                zIndex: -1,
                backdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }} />
              
              <div style={{
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <i className="pi pi-exclamation-circle" style={{ color: '#dc2626', fontSize: '24px' }} />
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: '8px'
                }}>
                  Delete Entry?
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  This action cannot be undone
                </p>
              </div>

              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                <GlobalButton
                  label="Delete"
                  onClick={handleConfirmDelete}
                  variant="danger"
                  size="small"
                  fullWidth
                  loading={isDeleting}
                  disabled={isDeleting}
                />
                <GlobalButton
                  label="Cancel"
                  onClick={handleCancelDelete}
                  variant="outline"
                  size="small"
                  fullWidth
                  disabled={isDeleting}
                />
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default DeleteModal;